import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { RealTimeProvider } from './context/RealTimeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import { IOSInstallPrompt } from './components/IOSInstallPrompt';
import { PWAInstallBanner, UpdateBanner, OfflineBanner } from './components/PWAComponents';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeNew from './pages/EmployeeNew';
import EmployeeDetail from './pages/EmployeeDetail';
import Vehicles from './pages/Vehicles';
import VehicleNew from './pages/VehicleNew';
import VehicleDetail from './pages/VehicleDetail';
import Map from './pages/Map';
import Planning from './pages/Planning';
import PlanningGenerator from './pages/PlanningGenerator';
import PlanningGeneratorResponsables from './pages/PlanningGeneratorResponsables';
import Tasks from './pages/Tasks';
import Incidents from './pages/Incidents';
import Warnings from './pages/Warnings';
import PerformanceImport from './pages/PerformanceImport';
import Notifications from './pages/Notifications';
import Messages from './pages/Messages';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import Maintenance from './pages/Maintenance';
import ActivityLog from './pages/ActivityLog';
import DataManagement from './pages/DataManagement';
import PendingAccess from './pages/PendingAccess';
import PendingUsers from './pages/PendingUsers';
import '@/App.css';
import '@/index.css';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <RealTimeProvider>
          {/* PWA Components */}
          <OfflineBanner />
          <UpdateBanner />
          <IOSInstallPrompt />
          <PWAInstallBanner />
        
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
          
          <Route
            path="/pending-access"
            element={
              <ProtectedRoute allowPending>
                <PendingAccess />
              </ProtectedRoute>
            }
          />
          
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <Layout>
                  <Employees />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/employees/new"
            element={
              <ProtectedRoute roles={['admin', 'rh']}>
                <Layout>
                  <EmployeeNew />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/employees/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmployeeDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vehicles"
            element={
              <ProtectedRoute>
                <Layout>
                  <Vehicles />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vehicles/new"
            element={
              <ProtectedRoute roles={['admin', 'responsable']}>
                <Layout>
                  <VehicleNew />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vehicles/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <VehicleDetail />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/map"
            element={
              <ProtectedRoute>
                <Layout>
                  <Map />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/planning"
            element={
              <ProtectedRoute>
                <Layout>
                  <Planning />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/planning/generator"
            element={
              <ProtectedRoute roles={['admin', 'responsable']}>
                <Layout>
                  <PlanningGenerator />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/planning/generator/responsables"
            element={
              <ProtectedRoute roles={['admin', 'responsable']}>
                <Layout>
                  <PlanningGeneratorResponsables />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <Layout>
                  <Tasks />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/incidents"
            element={
              <ProtectedRoute>
                <Layout>
                  <Incidents />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/warnings"
            element={
              <ProtectedRoute roles={['admin', 'rh', 'responsable']}>
                <Layout>
                  <Warnings />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/performance/import"
            element={
              <ProtectedRoute roles={['admin', 'rh', 'responsable']}>
                <Layout>
                  <PerformanceImport />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Layout>
                  <Notifications />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Layout>
                  <Messages />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <Admin />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/pending-users"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <PendingUsers />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/maintenance"
            element={
              <ProtectedRoute>
                <Layout>
                  <Maintenance />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/activity-log"
            element={
              <ProtectedRoute roles={['admin']}>
                <Layout>
                  <ActivityLog />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/data-management"
            element={
              <ProtectedRoute>
                <Layout>
                  <DataManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
        </RealTimeProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;